import * as React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';

import { approvePruneFlag, dismissPruneFlag, fetchPruningQueue } from '@src/api';

import PruningPreviewDialog from './PruningPreviewDialog';
import { flagSummary, spendLine, testLabel } from './pruningCopy';

// Loop #5 review surface. Each ad is compared with its ad-set siblings over the
// same weeks; it is flagged when it bought clearly fewer sales (or appointments,
// or self-books) per dollar, once it has spent enough to be judged.
//   Ready     — also flagged a week ago (or paused once before), first in line
//               in its set: pause it.
//   Watching  — flagged once, recovering lately, or another ad in the set goes first.
//   Tracking  — leads not reaching us / funnel data broken: never paused.
const SEVERITY_COLOR = { critical: 'error', high: 'error', medium: 'warning', low: 'default' };

// Facebook allows one edit per ad about every 30 seconds; pausing faster left an
// ad paused without its "-PAUSED-" label in Ads Manager (2026-09-07 batch).
const PAUSE_SPACING_MS = 30000;

const TABS = [
  { key: 'ready', label: 'Ready to pause' },
  { key: 'watching', label: 'Watching' },
  { key: 'tracking', label: 'Check tracking' },
];

const TAB_HELP = {
  ready: 'Flagged on two checks a week apart (or paused once before and flagged again), not recovering, and first in line in its ad set. These are the ones to pause.',
  watching: 'Flagged, but not ready yet: first flagged this week, recovering since the judged weeks, or another ad in the same set is worse. Nothing to do yet.',
  tracking: "Leads aren't reaching us or the client's funnel data is broken, so these ads can't be judged. Fix the tracking — never pause on these.",
};

export default function PruningQueue({ clientIds = [] }) {
  const [rows, setRows] = React.useState([]);
  const [trackingRows, setTrackingRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [busy, setBusy] = React.useState({}); // uid -> 'approving' | 'dismissing'
  const [notice, setNotice] = React.useState(null);
  const [activeFlag, setActiveFlag] = React.useState(null);
  const [tab, setTab] = React.useState('ready');
  const [cooldownUntil, setCooldownUntil] = React.useState(0);
  const [now, setNow] = React.useState(Date.now());

  const clientKey = clientIds.join(',');

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [flags, tracking] = await Promise.all([
        fetchPruningQueue({}),
        fetchPruningQueue({ view: 'tracking' }),
      ]);
      const ids = clientKey ? clientKey.split(',') : [];
      const scope = (resp) => {
        const data = Array.isArray(resp?.data) ? resp.data : [];
        return ids.length ? data.filter((r) => ids.includes(String(r.client_id))) : data;
      };
      setRows(scope(flags));
      setTrackingRows(scope(tracking));
    } catch (err) {
      console.error('Failed to load pruning queue:', err);
      setError('Unable to load the pruning queue.');
    } finally {
      setLoading(false);
    }
  }, [clientKey]);

  React.useEffect(() => {
    load();
  }, [load]);

  // Tick only while a cooldown is running, to drive the "Wait Ns" label.
  React.useEffect(() => {
    if (cooldownUntil <= Date.now()) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [cooldownUntil]);

  const cooldownLeft = Math.max(0, Math.ceil((cooldownUntil - now) / 1000));

  const act = async (uid, kind) => {
    setBusy((b) => ({ ...b, [uid]: kind }));
    try {
      if (kind === 'approving') {
        await approvePruneFlag({ uid });
        setCooldownUntil(Date.now() + PAUSE_SPACING_MS);
        setNow(Date.now());
      } else {
        await dismissPruneFlag({ uid });
      }
      setRows((rs) => rs.filter((r) => r.uid !== uid));
      setTrackingRows((rs) => rs.filter((r) => r.uid !== uid));
      setActiveFlag((f) => (f && f.uid === uid ? null : f));
      setNotice(
        kind === 'approving'
          ? 'Ad paused on Facebook. The next pause unlocks in 30 seconds so its label is applied.'
          : 'Flag dismissed. This ad stays out of the queue for 4 weeks.',
      );
    } catch (err) {
      console.error('Pruning action failed:', err);
      setNotice('Action failed — please retry.');
    } finally {
      setBusy((b) => {
        const next = { ...b };
        delete next[uid];
        return next;
      });
    }
  };

  const ready = rows.filter((r) => r.would_pause);
  const watching = rows.filter((r) => !r.would_pause);
  const visible = { ready, watching, tracking: trackingRows }[tab];
  const counts = { ready: ready.length, watching: watching.length, tracking: trackingRows.length };

  if (loading) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <CircularProgress size={28} />
      </Box>
    );
  }
  if (error) {
    return <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>;
  }

  const pauseLabel = (b) => {
    if (b === 'approving') return 'Pausing…';
    if (cooldownLeft) return `Wait ${cooldownLeft}s`;
    return 'Pause';
  };

  return (
    <Box>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 1, borderBottom: 1, borderColor: 'divider' }}>
        {TABS.map((t) => (
          <Tab key={t.key} value={t.key} label={`${t.label} (${counts[t.key]})`} />
        ))}
      </Tabs>
      <Typography variant="body2" color="text.secondary" sx={{ px: 2, pt: 1.5 }}>
        {TAB_HELP[tab]}
      </Typography>
      {notice ? (
        <Alert severity="info" onClose={() => setNotice(null)} sx={{ m: 1 }}>{notice}</Alert>
      ) : null}

      {!visible.length ? (
        <Box sx={{ p: 5, textAlign: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Nothing here</Typography>
          <Typography variant="body2" color="text.secondary">
            {tab === 'tracking'
              ? 'No tracking problems found.'
              : 'No ads in this list right now.'}
          </Typography>
        </Box>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Ad</TableCell>
              <TableCell>{tab === 'tracking' ? 'Problem' : 'Why it’s flagged'}</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {visible.map((r) => {
              const m = r.metrics || {};
              const b = busy[r.uid];
              const isTracking = tab === 'tracking';
              return (
                <TableRow key={r.uid} hover sx={{ verticalAlign: 'top' }}>
                  <TableCell sx={{ width: '32%' }}>
                    <Stack direction="row" spacing={1} alignItems="flex-start">
                      <Chip size="small" label={r.severity} color={SEVERITY_COLOR[r.severity] || 'default'} />
                      <Box>
                        <Typography
                          variant="body2"
                          onClick={() => !isTracking && setActiveFlag(r)}
                          sx={{
                            fontWeight: 700,
                            wordBreak: 'break-word',
                            ...(isTracking ? {} : { cursor: 'pointer', color: 'primary.main', '&:hover': { textDecoration: 'underline' } }),
                          }}
                        >
                          {r.ad_name || r.ad_id || r.client_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {[r.client_name, r.ad_set_name, r.vertical].filter(Boolean).join(' · ')}
                        </Typography>
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    {isTracking ? (
                      <Typography variant="body2">{r.detail || r.title}</Typography>
                    ) : (
                      <>
                        <Stack direction="row" spacing={0.5} sx={{ mb: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
                          <Chip size="small" variant="outlined" label={testLabel(m)} />
                          {m.previously_paused ? (
                            <Tooltip title="We paused this ad before and someone switched it back on." arrow>
                              <Chip size="small" color="warning" label="Turned back on" />
                            </Tooltip>
                          ) : null}
                          {m.recovered ? (
                            <Tooltip title="Bad over the judged weeks, but level with its ad set since. Held until that slips." arrow>
                              <Chip size="small" color="success" variant="outlined" label="Recovering" />
                            </Tooltip>
                          ) : null}
                        </Stack>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{flagSummary(m)}</Typography>
                        {spendLine(m) ? (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {spendLine(m)}
                          </Typography>
                        ) : null}
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                          {r.detail}
                        </Typography>
                      </>
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      {!isTracking ? (
                        <Button size="small" variant="outlined" disabled={!!b} onClick={() => setActiveFlag(r)}>
                          Preview
                        </Button>
                      ) : null}
                      {tab === 'ready' ? (
                        <Button
                          size="small"
                          color="error"
                          variant="contained"
                          disabled={!!b || cooldownLeft > 0}
                          onClick={() => act(r.uid, 'approving')}
                        >
                          {pauseLabel(b)}
                        </Button>
                      ) : null}
                      <Button size="small" variant="text" disabled={!!b} onClick={() => act(r.uid, 'dismissing')}>
                        {b === 'dismissing' ? '…' : 'Dismiss'}
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
      <PruningPreviewDialog
        flag={activeFlag}
        busy={activeFlag ? busy[activeFlag.uid] : null}
        canPause={Boolean(activeFlag?.would_pause) && cooldownLeft === 0}
        onApprove={() => activeFlag && act(activeFlag.uid, 'approving')}
        onDismiss={() => activeFlag && act(activeFlag.uid, 'dismissing')}
        onClose={() => setActiveFlag(null)}
      />
    </Box>
  );
}
